import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence, useInView } from "framer-motion"
import { 
  TrendingUp, 
  TrendingDown,
  Users, 
  Clock, 
  Activity, 
  ShieldCheck, 
  Layers, 
  Globe,
  Flame,
  ArrowUpRight,
  FlaskConical,
  Shirt,
  Cog,
  Cpu,
  Package,
  Building2
} from "lucide-react"
import { Link } from "@tanstack/react-router"
import { MOCK_MARKET_OVERVIEW } from "@/services/mockTradeIntelligenceApi"
import { cn } from "@/lib/utils"
import { PanIndiaNetwork } from "./PanIndiaNetwork"

const TRENDING_PRODUCTS = [
  {
    id: "1",
    name: "Medium oils and preparations",
    code: "271019",
    value: 3279,
    change: 5.0,
    trend: [96, 94, 94.5, 95.5, 94, 97, 106, 100],
    icon: FlaskConical,
  },
  {
    id: "2",
    name: "Diamonds",
    code: "710239",
    value: 1150,
    change: -2.0,
    trend: [104, 98, 96, 91, 101, 95, 94, 97, 93],
    icon: Flame,
  },
  {
    id: "3",
    name: "Solar PV Modules",
    code: "854143",
    value: 2840,
    change: 8.4,
    trend: [96, 98, 101, 99, 102, 107, 114],
    icon: Cpu,
  },
  {
    id: "4",
    name: "Industrial Centrifuges",
    code: "847989",
    value: 1920,
    change: -1.5,
    trend: [106, 104, 105, 102, 101, 99, 97],
    icon: Cog,
  },
  {
    id: "5",
    name: "Woven Cotton Textiles",
    code: "620442",
    value: 1680,
    change: 4.2,
    trend: [100, 102, 101, 104, 107, 109, 112],
    icon: Shirt,
  },
  {
    id: "6",
    name: "Jute Yarns & Bags",
    code: "530310",
    value: 840,
    change: 3.1,
    trend: [99, 101, 100, 103, 105, 104, 108],
    icon: Package,
  },
  {
    id: "7",
    name: "Specialty Chemicals",
    code: "290220",
    value: 2150,
    change: -3.8,
    trend: [108, 104, 105, 99, 102, 98, 94],
    icon: FlaskConical,
  },
  {
    id: "8",
    name: "Precision Auto Components",
    code: "870829",
    value: 2490,
    change: 6.7,
    trend: [100, 102, 105, 104, 109, 112, 118],
    icon: Cog,
  },
];

const calculateGrowth = (item: { trend: number[]; change?: number }) => {
  if (item.change !== undefined) return item.change
  if (!item.trend || item.trend.length < 2) return 0
  const first = item.trend[0]
  const last = item.trend[item.trend.length - 1]
  return ((last - first) / first) * 100
}

// Mini trend sparkline matching high-precision financial terminal aesthetic (angular polyline, green for increase, red for decrease)
function TrendLineSparkline({
  data,
  color,
  strokeWidth = 1.25,
  isPositive: explicitIsPositive,
  id = "spark",
}: {
  data?: number[]
  color?: string
  strokeWidth?: number | string
  isPositive?: boolean
  id?: string
}) {
  const points = Array.isArray(data) && data.length >= 2 ? data : null
  const isPositive =
    explicitIsPositive !== undefined
      ? explicitIsPositive
      : points
      ? points[points.length - 1] >= points[0]
      : true

  // Vivid emerald jade for increase (+), vivid rose/crimson for decrease (-)
  const strokeColor = color || (isPositive ? "var(--graph-spark-pos, #059669)" : "var(--graph-spark-neg, #e11d48)")

  // Fallback angular polyline if data is empty or missing (prevents blank gap)
  if (!points) {
    const fallbackD = isPositive
      ? "M 2 20 L 22 22 L 40 18 L 54 6 L 68 12"
      : "M 2 6 L 22 18 L 38 10 L 52 18 L 68 20"
    return (
      <svg viewBox="0 0 72 26" className="w-18 h-6 sm:h-6.5 overflow-visible" aria-hidden="true">
        <path
          d={fallbackD}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            filter: isPositive
              ? "drop-shadow(0 1px 3px var(--graph-spark-pos-glow, rgba(5,150,105,0.25)))"
              : "drop-shadow(0 1px 3px var(--graph-spark-neg-glow, rgba(225,29,72,0.25)))"
          }}
        />
      </svg>
    )
  }

  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1

  // Map to SVG coordinates (width: 72, height: 26, padX: 2, padY: 3)
  const coords = points.map((v, i) => {
    const x = 2 + (i / (points.length - 1)) * 68
    const y = 3 + (1 - (v - min) / range) * 19
    return { x, y }
  })

  // Angular financial terminal polyline path (straight lines with rounded joints)
  const pathD = coords.reduce(
    (acc, p, i) => (i === 0 ? `M ${p.x.toFixed(1)} ${p.y.toFixed(1)}` : `${acc} L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`),
    ""
  )

  return (
    <svg viewBox="0 0 72 26" className="w-18 h-6 sm:h-6.5 overflow-visible" aria-hidden="true">
      {/* Clean, crisp financial sparkline stroke with subtle ambient glow */}
      <path
        d={pathD}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          filter: isPositive
            ? "drop-shadow(0 1px 3px var(--graph-spark-pos-glow, rgba(5,150,105,0.25)))"
            : "drop-shadow(0 1px 3px var(--graph-spark-neg-glow, rgba(225,29,72,0.25)))"
        }}
      />
    </svg>
  )
}


const MARQUEE_PRODUCTS = [...TRENDING_PRODUCTS, ...TRENDING_PRODUCTS]

// Animated number counter starting from initial (0)
function AnimatedCounter({
  value,
  prefix = "",
  suffix = "",
  decimals = 0,
}: {
  value: number
  prefix?: string
  suffix?: string
  decimals?: number
}) {
  const [displayNumber, setDisplayNumber] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-20px" })

  useEffect(() => {
    if (!isInView) return
    let startTimestamp: number | null = null
    const duration = 1200
    let animationFrameId: number

    // Reset count to 0 initially
    setDisplayNumber(0)

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp
      const progress = Math.min((timestamp - startTimestamp) / duration, 1)
      // Ease out quartic for natural deceleration
      const ease = 1 - Math.pow(1 - progress, 4)
      const current = ease * value

      setDisplayNumber(current)

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(step)
      } else {
        setDisplayNumber(value)
      }
    }

    animationFrameId = requestAnimationFrame(step)
    return () => cancelAnimationFrame(animationFrameId)
  }, [value, isInView])

  const formatted = decimals > 0 
    ? displayNumber.toFixed(decimals) 
    : Math.round(displayNumber).toLocaleString()

  return (
    <span ref={ref}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  )
}

// Mini Sparkline SVG component with smooth bezier curve and rise-from-origin animation
function Sparkline({ 
  data, 
  color = "var(--primary)",
  timeframeKey = "" 
}: { 
  data: number[]
  color?: string
  timeframeKey?: string
}) {
  if (!data || data.length < 2) return null
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const width = 76
  const height = 28
  const paddingX = 3
  const paddingTop = 3
  const paddingBottom = 3

  const points = data.map((val, idx) => {
    const x = paddingX + (idx / (data.length - 1)) * (width - 2 * paddingX)
    const y = height - paddingBottom - ((val - min) / range) * (height - paddingTop - paddingBottom)
    return { x, y }
  })

  // Generate smooth cubic bezier curve
  let lineD = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i]
    const p1 = points[i + 1]
    const cpX = (p0.x + p1.x) / 2
    lineD += ` C ${cpX.toFixed(1)} ${p0.y.toFixed(1)}, ${cpX.toFixed(1)} ${p1.y.toFixed(1)}, ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`
  }

  // Close path at the bottom origin (y = height) for the gradient area fill
  const areaD = `${lineD} L ${points[points.length - 1].x.toFixed(1)} ${height} L ${points[0].x.toFixed(1)} ${height} Z`
  const lastPoint = points[points.length - 1]
  const cleanId = color.replace(/[^a-zA-Z0-9]/g, "")

  return (
    <div className="relative overflow-visible">
      <svg 
        width={width} 
        height={height} 
        viewBox={`0 0 ${width} ${height}`} 
        className="overflow-visible"
      >
        <defs>
          <linearGradient id={`spark-grad-${cleanId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.32" />
            <stop offset="100%" stopColor={color} stopOpacity="0.0" />
          </linearGradient>
          <filter id={`spark-glow-${cleanId}`} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodColor={color} floodOpacity="0.25" />
          </filter>
        </defs>

        {/* Group raising smoothly from origin (bottom baseline y = height) */}
        <motion.g
          key={`${color}-${timeframeKey}-${data.join(",")}`}
          initial={{ scaleY: 0, opacity: 0 }}
          animate={{ scaleY: 1, opacity: 1 }}
          transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
          style={{ transformOrigin: `center ${height}px` }}
        >
          {/* Glowing Area Fill */}
          <path
            d={areaD}
            fill={`url(#spark-grad-${cleanId})`}
          />

          {/* Smooth Line Curve with stroke animation */}
          <motion.path
            d={lineD}
            fill="none"
            stroke={color}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={`url(#spark-glow-${cleanId})`}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          />

          {/* Clean Static End Point Marker */}
          <circle
            cx={lastPoint.x}
            cy={lastPoint.y}
            r="2.5"
            fill={color}
          />
          <circle
            cx={lastPoint.x}
            cy={lastPoint.y}
            r="1"
            fill="#ffffff"
          />
        </motion.g>
      </svg>
    </div>
  )
}

export function LiveTradeCommandCenter({ className }: { className?: string } = {}) {
  const [timeframe, setTimeframe] = useState<"today" | "month" | "all">("today")
  const overview = MOCK_MARKET_OVERVIEW.data

  const kpis = [
    {
      id: "rfq",
      label: "Active RFQ Inquiries",
      numericValue: timeframe === "today" ? 384 : timeframe === "month" ? 7104 : 32250,
      prefix: "",
      suffix: "",
      decimals: 0,
      change: "+18.2%",
      isPositive: true,
      sublabel: "Across 24 export categories",
      icon: Activity,
      sparkline: overview.kpis.activeRfqs.sparkline,
      color: "var(--primary)",
    },
    {
      id: "suppliers",
      label: "Verified Manufacturers",
      numericValue: timeframe === "today" ? 1680 : timeframe === "month" ? 1820 : 2450,
      prefix: "",
      suffix: "+",
      decimals: 0,
      change: "+12.5%",
      isPositive: true,
      sublabel: "Audited GST & Export licenses",
      icon: Users,
      sparkline: overview.kpis.verifiedSuppliers.sparkline,
      color: "#059669",
    },
    {
      id: "corridors",
      label: "Active Trade Corridors",
      numericValue: timeframe === "today" ? 42 : timeframe === "month" ? 68 : 114,
      prefix: "",
      suffix: "",
      decimals: 0,
      change: "+14.6%",
      isPositive: true,
      sublabel: "GCC, EU, US & ASEAN",
      icon: Globe,
      sparkline: [26, 29, 32, 35, 38, 40, 42],
      color: "#7c3aed",
    },
    {
      id: "sla",
      label: "Avg Quotation Turnaround",
      numericValue: 3.4,
      prefix: "",
      suffix: "h",
      decimals: 1,
      change: "99.2%",
      isPositive: true,
      sublabel: "From inquiry to quote",
      icon: Clock,
      sparkline: overview.kpis.avgQuotationTurnaroundHours.sparkline,
      color: "#d97706",
    },
  ]

  return (
    <section
      id="live-command-center"
      className={cn(
        "relative font-sans scroll-mt-12 md:scroll-mt-14 pb-20 md:pb-24",
        className
      )}
    >
      <div className="max-w-7xl xl:max-w-[clamp(1280px,92vw,1440px)] 2xl:max-w-[clamp(1280px,92vw,1560px)] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header & Controls */}
        <div className="flex flex-col items-center justify-center text-center mb-8 sm:mb-10 lg:mb-12">
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-[34px] lg:text-[40px] font-bold text-slate-900 dark:text-white tracking-tight leading-tight text-center">
              The pulse of Indian industry, <span className="italic font-serif text-primary">live.</span>
            </h2>
          </div>
        </div>
       
        {/* 2-Column Grid: Left: Trending Products (Circular Moving Up Marquee) | Right: Capacity Matrix */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5 items-stretch">
          
          {/* Left Column: Trending Products Card (Span 5) */}
          <div className="lg:col-span-5 card-luminous bg-white dark:bg-slate-900 !border-slate-200/80 dark:!border-slate-800 rounded-2xl p-3.5 sm:p-4 shadow-xs flex flex-col overflow-hidden h-[335px] sm:h-[345px]">
            {/* Header */}
            <div className="flex items-center justify-between pb-2 mb-1 border-b border-slate-100 dark:border-slate-800 shrink-0">
              <div className="flex items-center gap-2">
                <div className="size-6.5 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <TrendingUp className="size-3.5" />
                </div>
                <h3 className="font-extrabold text-[14px] sm:text-[15px] text-slate-900 dark:text-white tracking-tight">Trending Products</h3>
              </div>
            </div>

            {/* Table Column Headers */}
            <div className="flex items-center justify-between px-2 py-1 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-800/80 shrink-0">
              <span className="flex-1">Product</span>
              <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                <span className="w-18 text-center">Trend</span>
                <span className="w-16 sm:w-18 text-right">Change</span>
              </div>
            </div>

            {/* Vertical Upward Moving Circular Marquee */}
            <div className="relative flex-1 min-h-0 overflow-hidden">
              {/* Top and Bottom Gradient Fade Masks */}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-3 bg-gradient-to-b from-white via-white/80 dark:from-slate-900 to-transparent z-10" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3 bg-gradient-to-t from-white via-white/80 dark:from-slate-900 to-transparent z-10" />

              <div className="flex flex-col animate-marquee-up cursor-pointer">
                {MARQUEE_PRODUCTS.map((item, idx) => {
                  const growth = calculateGrowth(item)
                  const isPositive = growth >= 0
                  const formattedGrowth = `${isPositive ? "+" : ""}${growth.toFixed(2)}%`

                  return (
                    <Link
                      key={`${item.id}-${idx}`}
                      to="/explore/suppliers"
                      search={{ searchTerm: item.name }}
                      className="flex items-center justify-between py-1.5 sm:py-2 pr-2 border-b border-slate-100 dark:border-slate-800/70 last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40 rounded-lg transition-all duration-200 group gap-2"
                    >
                      {/* Product Name & HS Code with vertical accent pill */}
                      <div className="flex items-center min-w-0 pr-1 flex-1">
                        <span className="w-[2px] h-5 sm:h-6 rounded-full bg-slate-200 dark:bg-slate-700 group-hover:bg-primary transition-colors mr-2 shrink-0" />
                        <div className="flex flex-col min-w-0">
                          <span className="text-xs sm:text-[13px] font-bold text-slate-900 dark:text-slate-100 truncate group-hover:text-primary transition-colors leading-tight">
                            {item.name}
                          </span>
                          <span className="text-[10px] sm:text-[10.5px] text-slate-400 dark:text-slate-500 font-mono font-medium leading-none mt-0.5">
                            {item.code}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                        {/* Area glow trend sparkline */}
                        <div className="w-18 flex items-center justify-center">
                          <TrendLineSparkline data={item.trend} isPositive={isPositive} />
                        </div>

                        {/* Value & Change Column */}
                        <div className="w-16 sm:w-18 flex flex-col items-end justify-center text-right gap-0.5">
                          <span className="text-xs sm:text-[13px] font-bold text-slate-900 dark:text-white leading-tight font-mono tracking-tight">
                            {item.value.toLocaleString()}
                          </span>
                          <span
                            className="text-[10px] font-semibold leading-tight whitespace-nowrap"
                            style={{
                              color: isPositive
                                ? "var(--graph-spark-pos, #059669)"
                                : "var(--graph-spark-neg, #e11d48)"
                            }}
                          >
                            {formattedGrowth}
                          </span>
                        </div>
                      </div>
                    </Link>
                  )
                })}
              </div>
            </div>

            {/* Bottom Footer */}
            <div className="mt-1.5 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs shrink-0">
              <span className="text-[11px] text-slate-500 dark:text-slate-400 font-medium flex items-center gap-1.5">
                <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Updated live across 24 export categories
              </span>
            </div>
          </div>

          {/* Right Column: Buyer Demand vs. Manufacturer Capacity Matrix (Span 7) */}
          <div className="lg:col-span-7 card-luminous bg-white dark:bg-slate-900 !border-slate-200/80 dark:!border-slate-800 rounded-2xl p-3.5 sm:p-4 shadow-xs flex flex-col justify-between h-[335px] sm:h-[345px] overflow-hidden">
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 pb-2 mb-1 border-b border-slate-100 dark:border-slate-800 shrink-0">
                {/* Title */}
                <div className="flex items-center gap-2 shrink-0">
                  <div className="size-6.5 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <Activity className="size-3.5" />
                  </div>
                  <h3 className="font-extrabold text-[14px] sm:text-[15px] text-slate-900 dark:text-white tracking-tight">
                    Demand vs. Capacity
                  </h3>
                </div>

                {/* Legend items (Clean high-contrast labels) */}
                <div className="flex items-center gap-3 text-xs font-semibold shrink-0 ml-auto">
                  <span className="flex items-center gap-1.5 text-slate-800 dark:text-slate-200 text-[10.5px] font-bold">
                    <span
                      className="size-2 rounded-full shadow-xs"
                      style={{
                        backgroundColor: "var(--graph-demand-dot, #be001e)",
                        boxShadow: "0 0 0 2px var(--graph-demand-ring, rgba(190,0,30,0.22))"
                      }}
                    /> Demand
                  </span>
                  <span className="flex items-center gap-1.5 text-slate-800 dark:text-slate-200 text-[10.5px] font-bold">
                    <span
                      className="size-2 rounded-full shadow-xs"
                      style={{
                        backgroundColor: "var(--graph-capacity-dot, #0284c7)",
                        boxShadow: "0 0 0 2px var(--graph-capacity-ring, rgba(2,132,199,0.22))"
                      }}
                    /> Capacity
                  </span>
                </div>
              </div>

              {/* Subtitle */}
              <p className="text-[11.5px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5 mb-1 shrink-0 font-normal">
                See how buyer demand aligns with available manufacturing capacity
              </p>

              {/* Sectors list */}
              <div className="space-y-3 sm:space-y-3.5 my-auto">
                {overview.sectorCapacity.map((sector) => {
                  const unmetDemand = Math.max(0, sector.buyerDemandPercent - sector.supplierAvailabilityPercent)
                  const excessCapacity = Math.max(0, sector.supplierAvailabilityPercent - sector.buyerDemandPercent)
                  const isGap = unmetDemand > 0
                  const isSurplus = excessCapacity > 0
                  const diffPercent = isGap ? unmetDemand : isSurplus ? excessCapacity : 0

                  return (
                    <div key={sector.sectorId} className="flex flex-col gap-1">
                      {/* Sector Name */}
                      <span className="font-bold text-slate-900 dark:text-white truncate text-xs sm:text-[13px] leading-tight">
                        {sector.sectorName}
                      </span>

                      {/* Dual Horizontal Bars with End Percentages (Left) + Difference Metric (Right) */}
                      <div className="flex items-center justify-between gap-3 sm:gap-4">
                        {/* Dual Horizontal Bars */}
                        <div className="flex-1 min-w-0 space-y-1.5 pt-0.5">
                          {/* Buyer Demand Bar */}
                          <div className="relative w-full h-[5px] sm:h-[6px] flex items-center pr-9">
                            <div className="relative w-full h-full bg-slate-100 dark:bg-slate-800/80 rounded-full">
                              <motion.div
                                initial={{ width: 0 }}
                                whileInView={{ width: `${sector.buyerDemandPercent}%` }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, ease: "easeOut" }}
                                className="relative h-full flex items-center"
                              >
                                <div
                                  className="w-full h-full rounded-full"
                                  style={{
                                    background: "linear-gradient(90deg, var(--graph-demand-from, #be001e) 0%, var(--graph-demand-to, #ff3355) 100%)",
                                    boxShadow: "0 0 8px var(--graph-demand-glow, rgba(190,0,30,0.28))"
                                  }}
                                />
                                <span
                                  className="absolute left-[calc(100%+5px)] font-mono text-[10px] sm:text-[10.5px] font-bold whitespace-nowrap leading-none"
                                  style={{ color: "var(--graph-demand-text, #be001e)" }}
                                >
                                  {sector.buyerDemandPercent}%
                                </span>
                              </motion.div>
                            </div>
                          </div>

                          {/* Manufacturer Capacity Bar */}
                          <div className="relative w-full h-[5px] sm:h-[6px] flex items-center pr-9">
                            <div className="relative w-full h-full bg-slate-100 dark:bg-slate-800/80 rounded-full">
                              <motion.div
                                initial={{ width: 0 }}
                                whileInView={{ width: `${sector.supplierAvailabilityPercent}%` }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.08, ease: "easeOut" }}
                                className="relative h-full flex items-center"
                              >
                                <div
                                  className="w-full h-full rounded-full"
                                  style={{
                                    background: "linear-gradient(90deg, var(--graph-capacity-from, #0284c7) 0%, var(--graph-capacity-to, #38bdf8) 100%)",
                                    boxShadow: "0 0 8px var(--graph-capacity-glow, rgba(2,132,199,0.26))"
                                  }}
                                />
                                <span
                                  className="absolute left-[calc(100%+5px)] font-mono text-[10px] sm:text-[10.5px] font-bold whitespace-nowrap leading-none"
                                  style={{ color: "var(--graph-capacity-text, #0284c7)" }}
                                >
                                  {sector.supplierAvailabilityPercent}%
                                </span>
                              </motion.div>
                            </div>
                          </div>
                        </div>

                        {/* Right End: Capacity Difference Metric (Unboxed, High-Impact) */}
                        <div className="shrink-0 flex items-center justify-end gap-1.5 text-right">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 font-mono font-bold text-xs sm:text-[12.5px] tracking-tight",
                              isGap
                                ? "text-rose-600 dark:text-rose-400"
                                : isSurplus
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-slate-500 dark:text-slate-400"
                            )}
                          >
                            {isGap ? (
                              <TrendingUp className="size-3.5 stroke-[2.5]" />
                            ) : isSurplus ? (
                              <TrendingDown className="size-3.5 stroke-[2.5]" />
                            ) : null}
                            <span>{diffPercent}%</span>
                          </span>
                          <span className="text-[10.5px] sm:text-[11.5px] font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
                            {isGap ? "capacity gap" : isSurplus ? "capacity surplus" : "balanced"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-1.5 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs shrink-0">
              <span className="flex items-center gap-1.5 text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 font-medium truncate mr-2">
                <ShieldCheck className="size-3.5 text-emerald-600 shrink-0" />
                <span>Capacity verified via factory audits &amp; export dispatch logs</span>
              </span>
            </div>
          </div>

        </div>

        {/* 2-Column Grid 2: Pan-India Network (Map & Regional Breakdown) */}
        <div className="mt-6 sm:mt-8">
          <PanIndiaNetwork />
        </div>

      </div>
    </section>
  )
}
